import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const UAEPage = () => {
  const uaeData = {
    name: 'United Arab Emirates',
    region: 'GCC',
    color: '#A060B0',
    metaTitle: 'Pharma & Nutraceutical Registration in UAE | MoHAP & Health Authorities | eCTD Dossier Requirements',
    metaDescription: 'Find out how to register pharmaceutical and nutraceutical products in the UAE. MoHAP requirements, eCTD dossier format, registration timelines, import/registration pathways for supplements and high-value export opportunities in a dynamic Gulf market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in the UAE',
      subtitle: 'A dynamic Gulf-hub market with high healthcare spend, advanced regulatory infrastructure and mandatory eCTD-based medicine registration - strong gateway for regional launch.',
      cta: 'Start Your UAE Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~10.5 million' },
      { label: 'Income Level', value: 'High-income with large expatriate presence' },
      { label: 'Health Expenditure', value: 'Among highest per-capita in region' },
      { label: 'eCTD Adoption', value: 'Mandatory since July 2016' },
      { label: 'Registration Timeline', value: '45 working days (ideal conditions)' },
      { label: 'Certificate Validity', value: '5 years' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Gateway Gulf Market + Export Potential',
      stats: [
        'MoHAP registration via eCTD format takes 45 working days under ideal conditions',
        'eCTD mandatory since July 2016 - reflects regulatory maturity and international alignment',
        'UAE is highly import-dependent: over 80% of pharmaceuticals in GCC are imported',
        'UAE acts as re-export/regional hub for wider Gulf and MENA markets',
        'Each emirate (Abu Dhabi, Dubai) has health authorities coordinating with federal MoHAP',
        'Growing nutraceutical/wellness market driven by expatriate and affluent local populations'
      ],
      opportunities: [
        'Premium high-visibility market serving as GCC launch pad',
        'Strong regulatory systems with digital portal and eCTD alignment',
        'Gateway role offers regional distribution and credibility',
        'Wellness and lifestyle trends create demand for premium supplements'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Lifestyle-Driven NCDs & Wellness Growth',
      subtitle: 'UAE faces high levels of lifestyle diseases common across GCC states, combined with growing wellness market.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'High levels of overweight/obesity across population',
            'Elevated diabetes prevalence',
            'Significant cardiovascular disease burden',
            'Lifestyle and dietary factors driving NCD growth',
            'Pharma demand: Antidiabetics, antihypertensives, lipid-lowering therapies',
            'Pharma demand: Renal/cardiovascular medicines, chronic disease management',
            'Specialty therapies: Oncology, biologics for hospital/clinic supply'
          ]
        },
        {
          name: 'Wellness & Premium Health Market',
          items: [
            'Growing wellness and nutritional supplement market',
            'Urban professionals and expatriates demand premium health products',
            'Fitness and lifestyle trends driving supplement adoption',
            'High-income population willing to pay for quality',
            'Nutra opportunities: Metabolic health, joint support, premium vitamins/minerals',
            'Nutra opportunities: Targeted lifestyle blends, weight-management, immunity support',
            'Premium positioning and branding essential for success'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Health & Prevention (MoHAP)',
      description: 'MoHAP handles pharmaceutical product registration via online service. Each emirate (Abu Dhabi, Dubai) has health authorities (DHA) that coordinate with federal MoHAP for approvals. UAE adopted eCTD format for new medicine registrations from July 2016 onward, with Gulf/ICH CTD alignment.',
      pharma: {
        timeline: '45 working days for conventional pharmaceutical product registration (ideal conditions). May extend based on dossier completeness, queries, pricing review or local tests. Registrations valid for five years.',
        requirements: [
          'Applicants must be licensed by MoHAP and act via UAE-registered warehouses',
          'Local representation required - UAE-licensed agent/MAH mandatory',
          'Mandatory eCTD format for conventional/biological medicines',
          'Submissions must conform to Module 1 GCC specification and CTD format',
          'Application via MoHAP smart app or website (UAE PASS login)',
          'Study of registration file, pricing discussion, stability & bioequivalence follow-up',
          'Full eCTD dossier (Modules 1-5) per GCC/ICH standards',
          'GMP certificate, CPP/free sale certificate required',
          'Bioequivalence study for generics',
          'Label artwork (Arabic + English)',
          'Certificates valid for five years after committee approval'
        ],
        insight: 'UAE eCTD requirement since 2016 demonstrates regulatory maturity. Digital portal and GCC/ICH alignment make process predictable. Service completion target is 45 working days but practical timelines vary. Registration certificates valid 5 years with renewal via eCTD sequences.'
      },
      nutra: {
        timeline: 'Varies based on product classification and documentation completeness. Faster than pharmaceutical route if correctly classified as food supplement.',
        requirements: [
          'Nutraceuticals and food-supplements under MoHAP Food & Health Product regulation',
          'Registration requirements mirror GCC harmonised rules',
          'CTD-like file may be required for health products claiming physiological benefit',
          'Correct classification critical: medicinal territory triggers pharma registration',
          'Supplement route requires: local importer, full composition, COA',
          'Label artwork (Arabic + English) mandatory',
          'Halal/alcohol-free certification where applicable',
          'Free sale certificate from country of origin',
          'Samples when requested by MoHAP-lab testing',
          'Premium market: positioning, branding and compliance matter significantly'
        ],
        insight: 'UAE wellness segment is premium - correct classification prevents costly pharma route. If dosage form, claims or nutrient levels move into medicinal territory, full pharmaceutical registration applies. For supplement route, ensure local importer in place and all documentation meets GCC standards.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in UAE (Mandatory eCTD)',
      steps: [
        'Local Agent / MAH - Applicants must be licensed by MoHAP and act via UAE-registered warehouses',
        'Dossier Format - eCTD mandatory for conventional/biological medicines since July 2016',
        'Application Submission - Via MoHAP smart app or website (UAE PASS login required)',
        'Complete eCTD dossier (Modules 1-5) per GCC/ICH standards',
        'Module 1: GCC specification, UAE-specific administrative requirements',
        'Modules 2-5: Quality/CMC, non-clinical, clinical per international CTD standards',
        'GMP certificate from approved authorities',
        'CPP/Free Sale Certificate from country of origin',
        'Bioequivalence study (for generic medicines)',
        'Label artwork in Arabic and English',
        'Pricing submission (if applicable)',
        'Registration file study, pricing discussion, stability & bioequivalence follow-up',
        'Service completion: 45 working days target (may extend with queries)',
        'Committee approval - Certificate valid for five years',
        'Renewal & Variations - Submitted via eCTD sequences after 5-year period'
      ]
    },
    
    nutraPathway: {
      title: 'Dietary Supplements & Wellness Products in the UAE',
      steps: [
        'Classification determination - Nutraceuticals/food-supplements under MoHAP Food & Health Product regulation',
        'Correct pathway selection critical to avoid pharmaceutical classification',
        'Supplement route requirements: Local importer/agent with UAE licence',
        'Composition sheet with active/nutrient levels (quantitative)',
        'COA (certificate of analysis) for finished product',
        'Manufacturer GMP/ISO/HACCP certificate',
        'Label artwork bilingual (Arabic + English), compliant with UAE/MENA rules',
        'Free Sale Certificate from country of origin',
        'Halal/alcohol-free certification where applicable',
        'Samples when requested by MoHAP for lab testing',
        'Premium positioning: Branding and compliance essential in high-income market',
        'CTD-like file may be required for health products with physiological benefit claims',
        'Avoid medicinal claims, dosage forms or high nutrient levels that trigger pharma route'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (MoHAP - eCTD)': [
        'MoHAP application form (smart app login via UAE PASS)',
        'Full eCTD dossier (Modules 1-5) per GCC/ICH standards',
        'Local MAH/agent licence and warehouse licence',
        'GMP certificate from approved authorities',
        'CPP/Free Sale Certificate',
        'Bioequivalence study (for generics)',
        'Label artwork (Arabic + English)',
        'Pricing submission (if applicable)',
        'Stability data for regional conditions'
      ],
      'Supplements / Health Products': [
        'Composition sheet with active/nutrient levels',
        'COA (finished product)',
        'Manufacturer GMP/ISO/HACCP certificate',
        'Label artwork bilingual, UAE/MENA compliant',
        'Free Sale Certificate from country of origin',
        'Local importer/agent licence',
        'Halal/alcohol-free certification (where applicable)',
        'Samples when requested by MoHAP-lab testing'
      ]
    },
    
    whyThisCountry: {
      title: 'Why UAE Should Be Part of Your Gulf Strategy',
      reasons: [
        'High-spend market with premium purchasing - healthcare and wellness spend among highest in region',
        'Gateway role: Success offers visibility, regional distribution (MENA/East Africa) and credibility',
        'Regulatory maturity: eCTD, digital portal, GCC/ICH alignment make process predictable',
        'Dossier assets reusable for GCC cluster (Saudi, Kuwait, Qatar, Oman, Bahrain)',
        'Wellness and nutraceutical demand: High-income, socially-conscious population plus expatriates',
        'Export advantage: High import reliance (80%+ pharma) creates opportunities for quality suppliers',
        'Strategic launch pad for broader regional penetration'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, statins, specialty therapies (oncology, biologics), hospital/clinic supply lines',
        'Nutra: Premium wellness formulations (metabolic health, joint & bone, immunity), high-end vitamins & minerals, weight-management supplements for affluent consumers'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter the UAE',
      services: [
        'Portfolio Prioritisation - Identify SKUs matching UAE demand, premium positioning, regulatory ease',
        'eCTD Dossier Preparation & Submission - Align to UAE/GCC Module 1, ensure completeness, prepare bilingual labels',
        'UAE PASS/Portal Implementation - Navigate smart app requirements and digital submission process',
        'Supplement Strategy - Classify correctly (food vs medicine), adapt formulations and labels to UAE regulation',
        'Local Partner Coordination - Facilitate importer/agent selection and warehouse licensing',
        'Lifecycle Planning - Build reusable dossier assets for broader GCC cluster',
        'Localization Support - Arabic language deliverables, pricing strategy, distribution mapping'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in the UAE?',
        answer: 'The Ministry of Health & Prevention (MoHAP) handles the registration and marketing of pharmaceuticals nationwide, with coordination from emirate-level health authorities (DHA in Dubai, HAAD in Abu Dhabi).'
      },
      {
        question: 'Is eCTD mandatory in UAE?',
        answer: 'Yes - UAE requires new medicine registrations in eCTD format (mandatory from 1 July 2016) aligning with GCC/ICH standards.'
      },
      {
        question: 'What is the timeline for medicine registration?',
        answer: 'For conventional pharmaceuticals: MoHAP lists 45 working days service completion for registration under ideal conditions. Practical timelines may extend based on dossier completeness and queries.'
      },
      {
        question: 'How are supplements regulated?',
        answer: 'Supplements/health-products fall under food/special-foods or health-products regulation. Correct classification and documentation (COA, composition, importer license, parallel to GCC standards) is essential to avoid pharmaceutical route.'
      },
      {
        question: 'Can Indian-manufactured medicines/supplements be exported to UAE?',
        answer: 'Yes - provided the manufacturer holds GMP certification, dossiers are CTD/eCTD-ready, local MAH/agent is in place, and all import/label requirements are met.'
      }
    ]
  };

  return <CountryTemplate countryData={uaeData} />;
};
